import React, { useEffect, useMemo } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import dayjs from 'dayjs'
import styles from './MonthlyRangePicker.module.css'
import { Controller, useFormContext } from 'react-hook-form'
import { useDatePickerLocale } from 'hooks/useDatePickerLocale'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'

export const MonthlyRangePicker = () => {
  const { watch, setValue, control, triggerValidation } = useFormContext()
  const { setMaxAllowedOverdueDays } = useFormStepContext()

  const fromTsValue = watch('from_ts')
  const monthlyRange = watch('monthly_range') ?? [null, null]

  const fromTs = useMemo(() => (fromTsValue ? new Date(fromTsValue) : new Date()), [fromTsValue])

  const startDateValue = useMemo(() => {
    return monthlyRange[0] ? new Date(monthlyRange[0]) : null
  }, [monthlyRange])

  const endDateValue = useMemo(() => {
    return monthlyRange[1] ? new Date(monthlyRange[1]) : null
  }, [monthlyRange])

  const minDate = fromTs
  const maxDate = dayjs(fromTs).add(30, 'day').toDate()

  const datePickerLocale = useDatePickerLocale(true)

  const handleDateChange = async (date: Date) => {
    const [start, end] = monthlyRange
    const startDate = start ? new Date(start) : null
    const endDateVal = end ? new Date(end) : null

    if (startDate && endDateVal) {
      setValue('monthly_range', [null, null])
      await triggerValidation('deadline')
      return
    }

    if (startDate && !endDateVal) {
      if (dayjs(date).isSame(startDate, 'day')) {
        setValue('monthly_range', [null, null])
        await triggerValidation('deadline')
        return
      } else {
        const newRange = [startDate.toISOString(), date.toISOString()]
        setValue('monthly_range', newRange)

        // Calculate and set allowed_overdue_days when range is complete
        const maxDays = calculateMaxAllowedOverdueDays(newRange)
        setValue('allowed_overdue_days', '')
        setMaxAllowedOverdueDays(maxDays)

        await triggerValidation('deadline')
        return
      }
    }

    if (!startDate && !endDateVal) {
      setValue('monthly_range', [date.toISOString(), null])
      await triggerValidation('deadline')
      return
    }
  }

  const dayClassName = (date: Date) => {
    if (startDateValue && dayjs(date).isSame(startDateValue, 'day')) {
      return styles['react-datepicker__day--range-start']
    }
    if (endDateValue && dayjs(date).isSame(endDateValue, 'day')) {
      return styles['react-datepicker__day--range-end']
    }
    if (startDateValue && endDateValue && dayjs(date).isAfter(startDateValue) && dayjs(date).isBefore(endDateValue)) {
      return styles['react-datepicker__day--range-middle']
    }
    return ''
  }

  // Function to calculate max allowed overdue days based on selected range
  const calculateMaxAllowedOverdueDays = (selectedRange: string[]): number => {
    if (!selectedRange || selectedRange.length !== 2 || !selectedRange[0] || !selectedRange[1]) {
      return 8
    }

    const startDate = dayjs(selectedRange[0])
    const endDate = dayjs(selectedRange[1])

    // Calculate number of days in the range (including boundary days)
    const daysInRange = endDate.diff(startDate, 'day') + 1

    // Calculate: period (30) - number of days in range, but maximum 8 and minimum 2
    const maxDays = Math.max(2, Math.min(8, 30 - daysInRange + 1))

    return maxDays
  }

  useEffect(() => {
    if (!monthlyRange[0] && !monthlyRange[1]) {
      setValue('monthly_range', [fromTs.toISOString(), null])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Initialize allowed_overdue_days from existing data
  useEffect(() => {
    if (monthlyRange && monthlyRange.length === 2 && monthlyRange[0] && monthlyRange[1]) {
      const maxDays = calculateMaxAllowedOverdueDays(monthlyRange)
      setValue('allowed_overdue_days', '')
      setMaxAllowedOverdueDays(maxDays)
    }
  }, [monthlyRange, setValue, setMaxAllowedOverdueDays])

  return (
    <Controller
      name="monthly_range"
      control={control}
      rules={{
        validate: (value) => {
          if (!Array.isArray(value) || value.length !== 2) {
            return 'Please select a start and end date.'
          }

          const [start, end] = value

          if (!start || !end) {
            return 'Please select a start and end date.'
          }

          const startD = new Date(start)
          const endD = new Date(end)

          if (isNaN(startD.getTime()) || isNaN(endD.getTime())) {
            return 'Please select a valid start and end date.'
          }
          return true
        },
      }}
      as={
        <div className={styles.datepicker}>
          <DatePicker
            selected={endDateValue || startDateValue || null}
            onSelect={handleDateChange}
            startDate={startDateValue}
            endDate={endDateValue}
            onChange={() => {}}
            selectsStart
            minDate={minDate}
            maxDate={maxDate}
            locale={datePickerLocale}
            inline
            dayClassName={dayClassName}
          />
        </div>
      }
    />
  )
}

export default MonthlyRangePicker
